import { Image, useTranslation } from '@packages/utils';
import { TColumnType } from 'components/Base/Table/Table.types';
import AgeGroupesColumn from 'components/Shared/AgeGroupesColumn';
import { ParagraphEllipsis } from 'components/Shared/ParagraphEllipsis';
import {
  ENotificationType,
  useShowToastNotification,
} from 'components/Base/Notification';
import { useUpdateAttendanceMutation } from 'redux/apiSlice/registrants.slice';
import {
  ESortDirection,
  EWorkshopLanguage,
  TRtkErrorType,
} from 'data/types/generic.types';
import {
  EAttendanceColumnKey,
  EAttendanceStatus,
  ERegistrantsColumnKey,
  TAttendanceEditUI,
  TAttendanceListUI,
} from 'data/types/registrants.types';
import photographyAllowed from 'assets/images/photographyAllowed.svg';
import photographyNotAllowed from 'assets/images/photographyNotAllowed.svg';
import useGetTableColumnData from 'components/Base/Table/components/SearchFilter';
import { Flex } from 'antd';
import { Switch } from 'components/Base/Switch';
import {
  EWorkshopSeriesType,
  EWorkshopStandaloneType,
  EWorkshopStatus,
  TPartsUI,
} from 'data/types/workshops.types';
import ButtonGroup from 'components/Base/ButtonGroup/ButtonGroup';
import { useAppSelector } from 'redux/store';
import { useMemo } from 'react';
import { EUserType } from 'data/types/user.types';
import { useCheckPermission } from 'hooks/useCheckPermission';
import dayjs from 'dayjs';
import { combineDateAndTime } from 'helpers/utils';
import { ANSWER_MAX_LENGTH } from '../../WaitlistTab/WorkshopWaitList/WorkshopWaitList.data';

const ADMIN_TYPES = [
  EUserType.READY_ADMIN,
  EUserType.SUPER_ADMIN,
  EUserType.ELC_LEAD,
];

const useGetData = ({
  workshopId,
  workshopStatus,
  workshopType,
  sessions,
  onNotificationToggle,
}: {
  workshopId: string;
  workshopStatus?: EWorkshopStatus;
  workshopType?: EWorkshopStandaloneType | EWorkshopSeriesType;
  sessions?: TPartsUI[];
  onNotificationToggle?: (record: TAttendanceListUI, checked: boolean) => void;
}) => {
  const usersData = useAppSelector(state => state.userSlice);
  const { user } = usersData;

  const isAdmin = useMemo(() => {
    return user?.type?.some((type: EUserType) => ADMIN_TYPES.includes(type));
  }, [user]);

  const { t } = useTranslation();

  const { getTableColumnSearchProps } = useGetTableColumnData();
  const defaultSortParam = {
    sortField: EAttendanceColumnKey.LAST_NAME,
    sortDirection: ESortDirection.descend,
  };

  const { showToastNotification } = useShowToastNotification();

  const [updateAttendance] = useUpdateAttendanceMutation();

  const hasNotificationPermission = useCheckPermission(
    'WORKSHOP_REGISTRANTS_EDIT_BASIC_INFO',
  );
  const canEditNotifications = hasNotificationPermission || isAdmin;

  const isNotificationDisabled = useMemo(() => {
    if (
      workshopStatus === EWorkshopStatus.CLOSED ||
      workshopStatus === EWorkshopStatus.CANCELLED
    ) {
      return true;
    }
    if (sessions?.length) {
      const lastSessionStart = sessions
        .map(s =>
          s.date?.value
            ? combineDateAndTime(s.date.value, s.startTime)
            : undefined,
        )
        .filter(Boolean)
        .sort()
        .pop();
      if (lastSessionStart && dayjs().isAfter(dayjs(lastSessionStart))) {
        return true;
      }
    }
    return false;
  }, [workshopStatus, sessions]);

  const isUpdatable = (record: TAttendanceListUI): boolean => {
    const currentAttendance = record.attendances.find(
      attendance =>
        attendance.sessionAttendanceId === record.currentAttendanceSessionId,
    );

    return currentAttendance?.isUpdatable || false;
  };

  const isDisabled = (record: TAttendanceListUI): boolean => {
    return (
      !isUpdatable(record) ||
      workshopStatus === EWorkshopStatus.CLOSED ||
      workshopStatus === EWorkshopStatus.CANCELLED
    );
  };

  const handleSubmit = async ({
    registrantId,
    attendances,
    orientationKitReceived,
  }: {
    registrantId: string;
    attendances?: TAttendanceEditUI[];
    orientationKitReceived: boolean;
  }) => {
    try {
      await updateAttendance({
        attendances: attendances || [],
        registrantId,
        workshopId,
        orientationKitReceived,
      }).unwrap();
      showToastNotification({
        message: t(
          'descriptions.admin-portal.workshops.details.workshop-registrants.actions.edit.modal.SUCCESS_MESSAGE',
        ),
        type: ENotificationType.SUCCESS,
      });
    } catch (err) {
      showToastNotification({
        message: (err as TRtkErrorType)?.data?.message,
        type: ENotificationType.ERROR,
      });
    }
  };

  const handleAttendanceUpdate = async (
    record: TAttendanceListUI,
    updates: {
      attendanceStatus?: EAttendanceStatus;
      kitCount?: number;
      orientationKitReceived?: boolean;
    },
  ) => {
    const currentAttendance = record.attendances.find(
      attendance =>
        attendance.sessionAttendanceId === record.currentAttendanceSessionId,
    );

    if (!currentAttendance) return;

    const updatedAttendance = {
      attendanceId: currentAttendance.sessionAttendanceId,
      attendanceStatus:
        updates.attendanceStatus || currentAttendance.attendanceStatus,
      kitCount: updates.kitCount ?? currentAttendance.kitCount ?? 0,
      sessions: record.sessions || [],
    };

    await handleSubmit({
      registrantId: record.registrantId,
      attendances: [updatedAttendance],
      orientationKitReceived:
        updates.orientationKitReceived ?? record.binders.received,
    });
  };

  const materialsLanguageFilterData = [
    {
      text: 'Eng',
      value: EWorkshopLanguage.ENGLISH,
    },
    {
      text: 'Spa',
      value: EWorkshopLanguage.SPANISH,
    },
  ];

  const defaultColumns: TColumnType<TAttendanceListUI>[] = [
    {
      title: t(
        'descriptions.admin-portal.workshops.details.attendance-list.table.title.FIRST_NAME',
      ),
      dataIndex: 'firstName',
      key: EAttendanceColumnKey.FIRST_NAME,
      sorter: true,
      ...getTableColumnSearchProps<TAttendanceListUI>(),
    },
    {
      title: t(
        'descriptions.admin-portal.workshops.details.attendance-list.table.title.LAST_NAME',
      ),
      dataIndex: 'lastName',
      key: EAttendanceColumnKey.LAST_NAME,
      sorter: true,
      ...getTableColumnSearchProps<TAttendanceListUI>(),
    },
    {
      title: t(
        'descriptions.admin-portal.workshops.details.attendance-list.table.title.PHOTO',
      ),
      dataIndex: 'photo',
      key: EAttendanceColumnKey.PHOTO,
      sorter: true,
      render: (_, record) => (
        <div className="pl-2">
          {record.photo ? (
            <Image src={photographyAllowed} height={24} alt="" />
          ) : (
            <Image src={photographyNotAllowed} height={24} alt="" />
          )}
        </div>
      ),
    },
    {
      title: t(
        'descriptions.admin-portal.workshops.details.attendance-list.table.title.CHILDREN_NAMES',
      ),
      dataIndex: 'childrenNames',
      key: 'childrenNames',
      render: (_, record) => <p>{record?.childrenNames?.join(', ')}</p>,
    },
    {
      title: t(
        'descriptions.admin-portal.workshops.details.attendance-list.table.title.AGE_GROUP',
      ),
      dataIndex: 'ageGroup',
      key: ERegistrantsColumnKey.FILTER_BY_LANGUAGE_AND_AGE_GROUP,
      filters: [],
      filterMode: 'tree',
      filterMultiple: true,
      render: (_, { englishAgeGroups, spanishAgeGroups }) => {
        return (
          <AgeGroupesColumn
            englishSessions={englishAgeGroups}
            spanishSessions={spanishAgeGroups}
          />
        );
      },
    },
    {
      title: t(
        'descriptions.admin-portal.workshops.details.attendance-list.table.title.QUESTION_ANSWER',
      ),
      dataIndex: 'customQuestionAnswer',
      key: 'customQuestionAnswer',
      render: answer => (
        <ParagraphEllipsis
          text={answer}
          textMaxLength={ANSWER_MAX_LENGTH}
          fontFamily="Montserrat"
        />
      ),
      onCell: () => ({
        style: { minWidth: '250px' },
      }),
    },
    {
      title: t(
        'descriptions.admin-portal.workshops.details.attendance-list.table.title.MATERIALS_LANGUAGE',
      ),
      dataIndex: 'materialsLanguage',
      key: EAttendanceColumnKey.MATERIALS_LANGUAGE_FILTER,
      filters: materialsLanguageFilterData,
      onFilter: (value, record) =>
        record.materialsLanguage.indexOf(value as string) === 0,
      render: (_, record) => {
        return record.materialsLanguage === EWorkshopLanguage.ENGLISH
          ? 'Eng'
          : 'Spa';
      },
    },
    {
      title: t(
        'descriptions.admin-portal.workshops.details.attendance-list.table.title.ATTENDANCE',
      ),
      dataIndex: 'attendance',
      key: 'attendance',
      render: (_, record) => (
        <ButtonGroup
          options={[
            {
              label: 'Attended',
              value: EAttendanceStatus.ATTENDED,
            },
            {
              label: 'Absent',
              value: EAttendanceStatus.ABSENT,
            },
          ]}
          value={record.attendance || EAttendanceStatus.ATTENDED}
          size="small"
          disabled={isDisabled(record)}
          onChange={async value => {
            if (record.attendance === value) return;
            await handleAttendanceUpdate(record, {
              attendanceStatus: value as EAttendanceStatus,
              kitCount: value === EAttendanceStatus.ABSENT ? 0 : record.kits,
              orientationKitReceived:
                value === EAttendanceStatus.ABSENT
                  ? false
                  : record.binders.received,
            });
          }}
        />
      ),
    },
    {
      title: t(
        'descriptions.admin-portal.workshops.details.attendance-list.table.title.KITS',
      ),
      dataIndex: 'kits',
      key: 'kits',
      render: (_, record) => (
        <Flex gap={30} align="center">
          <Switch
            checkedChildren="Yes"
            unCheckedChildren="No"
            checked={record.kits >= 1}
            disabled={
              isDisabled(record) ||
              record.attendance === EAttendanceStatus.ABSENT
            }
            onChange={async checked => {
              const kitCount = checked ? 1 : 0;
              await handleAttendanceUpdate(record, { kitCount });
            }}
          />
        </Flex>
      ),
    },
    {
      title: t(
        'descriptions.admin-portal.workshops.details.attendance-list.table.title.BINDERS',
      ),
      dataIndex: 'binders',
      key: 'binders',
      render: (_, record) => {
        const notLabel =
          workshopType === EWorkshopSeriesType.READY_FOR_KINDERGARTEN
            ? 'Not Needed'
            : 'Not Required';

        return (
          <>
            {record.binders.requested ? (
              <Switch
                checkedChildren="Yes"
                unCheckedChildren="No"
                disabled={
                  isDisabled(record) ||
                  record.attendance === EAttendanceStatus.ABSENT
                }
                onChange={async () => {
                  await handleAttendanceUpdate(record, {
                    orientationKitReceived: !record.binders.received,
                  });
                }}
                checked={record.binders.received}
              />
            ) : (
              <>{notLabel}</>
            )}
          </>
        );
      },
    },
    {
      title: 'Notifications',
      dataIndex: 'notificationsEnabled',
      key: EAttendanceColumnKey.NOTIFICATIONS,
      render: (_, record) => (
        <Switch
          checkedChildren="On"
          unCheckedChildren="Off"
          checked={record.notificationsEnabled !== false}
          disabled={!canEditNotifications || isNotificationDisabled}
          onChange={checked => {
            onNotificationToggle?.(record, checked);
          }}
        />
      ),
    },
    {
      title: ' ',
      dataIndex: 'actions',
      key: 'actions',
      fixed: 'right',
      width: 50,
      render: () => null,
    },
  ];

  return { defaultColumns, defaultSortParam };
};

export default useGetData;
